import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Sparkles, Image as ImageIcon, Download, Copy, 
  Zap, Camera, Lightbulb, Clock, DollarSign
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AIImageGenerator = () => {
  const [prompt, setPrompt] = useState('');
  const [selectedModel, setSelectedModel] = useState('openai');
  const [numberOfImages, setNumberOfImages] = useState(1);
  const [referenceImage, setReferenceImage] = useState(null);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationTime, setGenerationTime] = useState(null);
  const [error, setError] = useState(null);

  const modelInfo = {
    openai: {
      name: 'OpenAI DALL-E (GPT Image 1)',
      icon: <Camera className="w-5 h-5" />,
      color: 'bg-green-100 text-green-700',
      speed: 'Slower (30-60s)',
      style: 'Photorealistic',
      bestFor: ['Product photography', 'Professional marketing', 'High detail'],
      examples: [
        'A professional product photo of crispy ragi bhujia in a white bowl on a marble countertop with natural lighting',
        'Photorealistic image of edamame seeds scattered on a wooden board with fresh herbs',
        'High-quality commercial photo of multigrain snacks in premium packaging'
      ]
    },
    gemini: {
      name: 'Gemini Nano Banana',
      icon: <Zap className="w-5 h-5" />,
      color: 'bg-purple-100 text-purple-700',
      speed: 'Faster (10-30s)',
      style: 'Vibrant & Versatile',
      bestFor: ['Quick iterations', 'Social media', 'Creative designs', 'Image editing'],
      examples: [
        'Vibrant illustration of healthy snacks arranged in a colorful pattern for Instagram',
        'Creative social media post showing edamame seeds with fun cartoon characters',
        'Artistic flat-lay of multigrain bhujia with bright colors and playful composition'
      ]
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        setReferenceImage(base64);
      };
      reader.readAsDataURL(file);
    }
  };

  const generateImage = async () => {
    if (!prompt.trim()) {
      setError('Please enter a prompt');
      return;
    }

    setIsGenerating(true);
    setError(null);
    setGeneratedImages([]);
    setGenerationTime(null);

    try {
      const { data } = await axios.post(
        `${API}/ai/generate-image`,
        {
          prompt,
          model: selectedModel,
          number_of_images: numberOfImages,
          reference_image_base64: referenceImage
        },
        { withCredentials: true }
      );

      setGeneratedImages(data.images);
      setGenerationTime(data.generation_time);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Image generation error:', err);
      }
      setError(err.response?.data?.detail || 'Failed to generate image. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadImage = (base64Image, index) => {
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${base64Image}`;
    link.download = `ai-generated-${selectedModel}-${index + 1}.png`;
    link.click();
  };

  const copyToClipboard = (base64Image) => {
    navigator.clipboard.writeText(`data:image/png;base64,${base64Image}`);
    alert('Image data URL copied to clipboard!');
  };

  const applyExamplePrompt = (examplePrompt) => {
    setPrompt(examplePrompt);
  };

  const currentModel = modelInfo[selectedModel];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-orange-500" />
            AI Image Generator
          </h2>
          <p className="text-gray-600 mt-1">
            Create stunning product images and marketing content with AI
          </p>
        </div>
        <Badge className={currentModel.color}>
          {currentModel.icon}
          <span className="ml-2">{currentModel.name}</span>
        </Badge>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Panel - Controls */}
        <div className="lg:col-span-1 space-y-4">
          {/* Model Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Choose AI Model</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <button
                onClick={() => setSelectedModel('openai')}
                className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                  selectedModel === 'openai'
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-green-300'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Camera className="w-5 h-5 text-green-600" />
                  <span className="font-semibold">OpenAI DALL-E</span>
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <p className="flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {modelInfo.openai.speed}
                  </p>
                  <p>Style: {modelInfo.openai.style}</p>
                </div>
              </button>

              <button
                onClick={() => setSelectedModel('gemini')}
                className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                  selectedModel === 'gemini'
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-purple-300'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-5 h-5 text-purple-600" />
                  <span className="font-semibold">Gemini Nano Banana</span>
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <p className="flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {modelInfo.gemini.speed}
                  </p>
                  <p>Style: {modelInfo.gemini.style}</p>
                </div>
              </button>
            </CardContent>
          </Card>

          {/* Best For */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Lightbulb className="w-4 h-4" />
                Best For
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                {currentModel.bestFor.map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <span className="text-orange-500">✓</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Example Prompts */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Example Prompts</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {currentModel.examples.map((example) => (
                <button
                  key={example.substring(0, 50)}
                  onClick={() => applyExamplePrompt(example)}
                  className="w-full text-left p-2 text-xs bg-gray-50 hover:bg-gray-100 rounded border border-gray-200 transition-colors"
                >
                  {example}
                </button>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Right Panel - Generation */}
        <div className="lg:col-span-2 space-y-4">
          {/* Prompt Input */}
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Describe what you want to create
                </label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="E.g., Professional product photo of crispy ragi bhujia in a white bowl..."
                  className="w-full h-32 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              {selectedModel === 'openai' && (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Number of Images
                  </label>
                  <select
                    value={numberOfImages}
                    onChange={(e) => setNumberOfImages(Number(e.target.value))}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  >
                    <option value={1}>1 image</option>
                    <option value={2}>2 images</option>
                    <option value={3}>3 images</option>
                    <option value={4}>4 images</option>
                  </select>
                </div>
              )}

              {selectedModel === 'gemini' && (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Reference Image (Optional - for editing)
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                  {referenceImage && (
                    <p className="text-sm text-green-600 mt-2">✓ Reference image uploaded</p>
                  )}
                </div>
              )}

              <Button
                onClick={generateImage}
                disabled={isGenerating || !prompt.trim()}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white h-12 text-lg"
              >
                {isGenerating ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Generate Image
                  </>
                )}
              </Button>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Generated Images */}
          {generatedImages.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <ImageIcon className="w-5 h-5" />
                    Generated Images
                  </span>
                  {generationTime && (
                    <Badge variant="outline">
                      Generated in {generationTime}s
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {generatedImages.map((img, index) => (
                    <div key={`generated-${img.substring(0, 20)}-${index}`} className="space-y-2">
                      <div className="relative group">
                        <img
                          src={`data:image/png;base64,${img}`}
                          alt={`Generated ${index + 1}`}
                          className="w-full rounded-lg border-2 border-gray-200"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-lg flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                          <Button
                            onClick={() => downloadImage(img, index)}
                            className="bg-white text-gray-900 hover:bg-gray-100"
                            size="sm"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Download
                          </Button>
                          <Button
                            onClick={() => copyToClipboard(img)}
                            className="bg-white text-gray-900 hover:bg-gray-100"
                            size="sm"
                          >
                            <Copy className="w-4 h-4 mr-1" />
                            Copy
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIImageGenerator;
